import React, {useRef, useState} from 'react';

import {Checkmark, Content, DropDown, List, ListItem, Trigger} from '@kit';

import {cls, humanEnumeration} from '../../../../components/utils';
import {useCharacter} from '../character-context';
import type {RollMode} from '../model/conditions';
import {D20, withBonus} from '../model/dice';
import {sizeLabel} from '../model/labels';
import {ArmorClassModal} from './armor-class-modal';
import {ConditionsList} from './conditions-bar';
import {DeathSaveMarks} from './death-saves';
import {defenseFacts, FactList} from './defenses-block';
import {ROLL_MODES, RollButton, useRoll} from './dice-roller';
import {formatBonus, formatFeet} from './format';
import {GlossaryModal} from './glossary-modal';
import {HitPointsModal} from './hit-points-modal';
import {Measure} from './measure';
import {SpeedModal} from './speed-modal';
import {useWideScreen} from './use-wide-screen';

import css from './sheet.module.css';

const LONG_PRESS_DELAY = 500;

type StatCardProps = {
	label: string;
	title?: string;
	/** значение — слово, а не число: шрифт мельче, чтобы поместилось в плашку */
	isText?: boolean;
	/** плашка-переключатель: включена */
	pressed?: boolean;
	valueClassName?: string;
	/** плашка нажимается целиком */
	onClick?(): void;
	children: React.ReactNode;
};

type StatContentProps = Pick<StatCardProps, 'label' | 'isText' | 'valueClassName' | 'children'>;

const StatContent: React.FC<StatContentProps> = ({label, isText, valueClassName, children}) => (
	<>
		<span className={css.statLabel}>{label}</span>
		<span className={cls(css.statValue, isText && css.statText, valueClassName)}>{children}</span>
	</>
);

/** плашка показателя: заголовок сверху, значение снизу; одна и та же в ряду на широком экране и в строке на узком */
export const StatCard: React.FC<StatCardProps> = ({title, pressed, onClick, ...content}) =>
	onClick ? (
		<button
			type="button"
			className={cls(css.stat, css.statButton)}
			title={title}
			aria-pressed={pressed}
			onClick={onClick}
		>
			<StatContent {...content} />
		</button>
	) : (
		<div className={css.stat} title={title}>
			<StatContent {...content} />
		</div>
	);

type MenuCardProps = Omit<StatCardProps, 'onClick' | 'pressed'> & {
	/** содержимое выпадашки */
	menu: React.ReactNode;
};

/** плашка, по нажатию на которую открывается выпадашка */
const MenuCard: React.FC<MenuCardProps> = ({title, menu, ...content}) => (
	<DropDown className={css.statDrop}>
		<Trigger>
			<button type="button" className={cls(css.stat, css.statButton)} title={title}>
				<StatContent {...content} />
			</button>
		</Trigger>
		<Content className={css.statMenu}>{menu}</Content>
	</DropDown>
);

export const ArmorClassCard: React.FC = () => {
	const {character} = useCharacter();
	const [isOpen, setOpen] = useState(false);

	return (
		<>
			<StatCard label="Класс брони" title="Из чего складывается КБ" onClick={() => setOpen(true)}>
				{character.armorClass}
			</StatCard>
			{isOpen && <ArmorClassModal onClose={() => setOpen(false)} />}
		</>
	);
};

/** короткая плашка ПЗ: текущие вместе с временными и максимум; при 0 ПЗ — испытания против смерти */
export const HitPointsCard: React.FC = () => {
	const {character} = useCharacter();
	const [isOpen, setOpen] = useState(false);
	const temporary = character.currentTemporaryHitPoints;

	return (
		<>
			<StatCard
				label="ПЗ"
				title={
					temporary
						? `Управление ПЗ: ${character.currentHitPoints} + ${temporary} временных`
						: 'Управление ПЗ'
				}
				valueClassName={cls(temporary > 0 && css.statAccent)}
				onClick={() => setOpen(true)}
			>
				{character.currentHitPoints > 0 ? (
					`${character.currentHitPoints + temporary}/${character.maxHitPoints}`
				) : (
					<DeathSaveMarks small />
				)}
			</StatCard>
			{isOpen && <HitPointsModal onClose={() => setOpen(false)} />}
		</>
	);
};

export const SpeedCard: React.FC = () => {
	const {state, character} = useCharacter();
	const [isOpen, setOpen] = useState(false);
	const hasOverrides = Object.values(state.overrides.speeds || {}).some((speed) => speed != null);

	return (
		<>
			<StatCard
				label="Скорость"
				title="Все скорости"
				valueClassName={cls(hasOverrides && css.overridden)}
				onClick={() => setOpen(true)}
			>
				<Measure text={formatFeet(character.speeds.walk)} />
			</StatCard>
			{isOpen && <SpeedModal onClose={() => setOpen(false)} />}
		</>
	);
};

export const InspirationCard: React.FC = () => {
	const {state, update} = useCharacter();

	return (
		<StatCard
			label="Вдохновение"
			title="Героическое вдохновение"
			pressed={state.heroicInspiration}
			onClick={() => update({heroicInspiration: !state.heroicInspiration})}
		>
			<Checkmark checked={state.heroicInspiration} />
		</StatCard>
	);
};

export const ConditionsCard: React.FC = () => {
	const {state, character} = useCharacter();
	const count = state.conditions.length + (character.exhaustion > 0 ? 1 : 0);

	return (
		<MenuCard label="Состояния" valueClassName={cls(count > 0 && css.statDanger)} menu={<ConditionsList />}>
			{count}
		</MenuCard>
	);
};

/** на широком экране бросок кнопкой с выбором режима; на узком нажатие бросает, долгое нажатие открывает выбор */
export const InitiativeCard: React.FC = () => {
	const {character} = useCharacter();
	const isWide = useWideScreen();

	if (!isWide) return <InitiativePressCard />;
	return (
		<StatCard label="Инициатива">
			<RollButton
				title="Инициатива"
				bonus={character.initiative}
				context={{kind: 'initiative'}}
				className={css.statRoll}
			>
				{formatBonus(character.initiative)}
			</RollButton>
		</StatCard>
	);
};

const InitiativePressCard: React.FC = () => {
	const {character} = useCharacter();
	const roll = useRoll();
	const [isMenuOpen, setMenuOpen] = useState(false);
	const pressTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const isLongPress = useRef(false);
	const context = {kind: 'initiative'} as const;
	const advice = character.rollAdvice(context);
	const reasons = humanEnumeration(advice.reasons);

	const rollWith = (mode: RollMode) =>
		roll({title: 'Инициатива', formula: withBonus(D20, character.initiative), mode, context});

	const startPress = () => {
		isLongPress.current = false;
		pressTimer.current = setTimeout(() => {
			isLongPress.current = true;
			setMenuOpen(true);
		}, LONG_PRESS_DELAY);
	};
	const cancelPress = () => clearTimeout(pressTimer.current);

	return (
		<DropDown
			className={css.statDrop}
			selectMode="single"
			value={[advice.mode]}
			open={isMenuOpen}
			onOpenChange={(open) => !open && setMenuOpen(false)}
			onChange={([mode]) => {
				setMenuOpen(false);
				if (mode !== undefined) rollWith(mode as RollMode);
			}}
		>
			<Trigger>
				<button
					type="button"
					className={cls(css.stat, css.statButton)}
					title={`Бросить инициативу; долгое нажатие — выбор режима${reasons ? ` (${reasons})` : ''}`}
					onPointerDown={startPress}
					onPointerUp={cancelPress}
					onPointerLeave={cancelPress}
					onContextMenu={(event) => event.preventDefault()}
					onClick={(event) => {
						event.preventDefault();
						if (!isLongPress.current) rollWith(advice.mode);
						isLongPress.current = false;
					}}
				>
					<StatContent
						label="Инициатива"
						valueClassName={cls(
							advice.mode === 'advantage' && css.rollAdvantage,
							advice.mode === 'disadvantage' && css.rollDisadvantage,
						)}
					>
						{formatBonus(character.initiative)}
					</StatContent>
				</button>
			</Trigger>
			<Content>
				<div className={css.statMenuHeader}>
					<span className={css.rollMenuTitle}>Инициатива</span>
					{reasons && <span className={css.rollMenuWarning}>{reasons}</span>}
				</div>
				<List selectAppearance="selected" aria-label="Режим броска: Инициатива">
					{ROLL_MODES.map(({value, label}) => (
						<ListItem key={value} value={value}>
							{label}
						</ListItem>
					))}
				</List>
			</Content>
		</DropDown>
	);
};

/** защиты свёрнуты в плашку: список открывается по нажатию */
export const DefensesCard: React.FC = () => {
	const {character} = useCharacter();
	const facts = defenseFacts(character);

	return (
		<MenuCard
			label="Защиты"
			title="Устойчивости, невосприимчивости и преимущества"
			menu={facts.length ? <FactList facts={facts} /> : <span className={css.muted}>Защит нет.</span>}
		>
			{facts.length || '—'}
		</MenuCard>
	);
};

export const SizeCard: React.FC = () => {
	const {character} = useCharacter();
	const [isOpen, setOpen] = useState(false);
	const {size} = character;

	return (
		<>
			<StatCard label="Размер" title="Что даёт размер" isText={!!size} onClick={() => setOpen(true)}>
				{size ? sizeLabel(size) : '—'}
			</StatCard>
			{isOpen && <GlossaryModal id="size" header="Размер" onClose={() => setOpen(false)} />}
		</>
	);
};

export const ProficiencyBonusCard: React.FC = () => {
	const {character} = useCharacter();
	const [isOpen, setOpen] = useState(false);

	return (
		<>
			<StatCard label="Бонус умения" title="Что такое бонус умения" onClick={() => setOpen(true)}>
				{formatBonus(character.proficiencyBonus)}
			</StatCard>
			{isOpen && <GlossaryModal id="pb" header="Бонус умения" onClose={() => setOpen(false)} />}
		</>
	);
};
